import { PaginatedAgentsPayload } from 'app/modules/agents/requests';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { AGENT_QUERY_KEYS } from 'app/modules/agents/queries/keys';
import { post } from 'app/shared/utils/fetchr';
import {
  PaginatedAgentsResponse,
  ShortAgentResponse,
} from 'app/modules/agents/responses';
import { selectHasReadAgentsPermissions } from 'app/modules/session/selectors';
import { useSelector } from 'react-redux';
import { useMemo, useReducer } from 'react';
import { unionBy } from 'lodash';
import { AgentStatus } from 'app/modules/agents/models';

const getAgentSources = (
  data: PaginatedAgentsResponse,
  payload: PaginatedAgentsPayload,
): PaginatedAgentsResponse => {
  const filteredData = data.agents.filter((agent) => {
    if (
      payload.statuses &&
      !payload.statuses.includes(AgentStatus[agent.status])
    ) {
      return false;
    }

    if (
      payload.queue_ids &&
      !payload.queue_ids.some((q) => agent.accessible_queues.includes(q))
    ) {
      return false;
    }

    return true;
  });

  return {
    agents: filteredData,
    // Note that this is not the exact count since we have no way of filtering the values in the rest of the pages
    count: data.count - data.agents.length + filteredData.length,
  };
};

export const searchAgent = (payload: PaginatedAgentsPayload) =>
  post<PaginatedAgentsResponse>('/agents/autocomplete', payload);

export const useAgentSearch = (payload: PaginatedAgentsPayload) => {
  const queryClient = useQueryClient();
  const hasReadAgentsPermissions = useSelector(selectHasReadAgentsPermissions);
  const query = useQuery({
    enabled: Boolean(payload.limit > 0 && hasReadAgentsPermissions),
    queryFn: () => searchAgent(payload),
    queryKey: AGENT_QUERY_KEYS.getAgentSearch(payload),
    select: (data) => getAgentSources(data, payload),
  });

  // call to manually refresh agents from cached data
  const [, refreshAgents] = useReducer((x) => x + 1, 0);

  const cachedQueryData = queryClient.getQueriesData<PaginatedAgentsResponse>({
    queryKey: AGENT_QUERY_KEYS.getAgentSearch(),
  });

  const agents = useMemo(
    () =>
      cachedQueryData.reduce<ShortAgentResponse[]>((acc, i) => {
        const [, queryData] = i;
        if (queryData) {
          return unionBy(getAgentSources(queryData, payload).agents, acc, 'id');
        }
        return acc;
      }, []),
    [cachedQueryData, payload],
  );

  return {
    query,
    agents,
    refreshAgents,
  };
};
